import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Search,
  Loader2,
  Paperclip,
  Star,
  Reply,
  ReplyAll,
  Pin,
  UserPlus,
  Send,
  Check,
  Forward,
  Mail,
  MailOpen,
  Tag,
  X,
  BookOpenCheck,
  MailSearch,
  ListFilter,
  PenSquare,
  CalendarDays,
  Filter,
  Plus,
  MoreHorizontal,
  FolderInput,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getSupabase } from "@/lib/supabase";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { cn } from "@/lib/utils";
import { EmailHtmlBody } from "@/components/custom/email/EmailHtmlBody";
import {
  api,
  emailApi,
  type ClientEmail,
  type InboxGroup,
  type InboxMessage,
} from "@/lib/api";
import { EmailReplyModal } from "@/components/custom/EmailReplyModal";
import { MailboxFilterRulesDialog } from "@/components/custom/MailboxFilterRulesDialog";
import { filterHiddenGroups } from "@/lib/email/hiddenMailboxes";
import { useHiddenMailboxes } from "@/lib/userPrefs";
import { fromInboxMessage, toReplyEmail, type UnifiedEmail } from "@/lib/email/types";
import {
  avatarColor,
  categoryColor,
  EMAIL_TASK_STATUSES,
  emailTaskStatusLabel,
  firstNameOf,
  initialsFor,
  internalDeliveryBadge,
  shortWhen,
  statusChipClass,
} from "@/lib/email/format";
import { useEmailActions, type BulkEmailAction } from "@/components/custom/email/useEmailActions";
import { useEmailSelection } from "@/components/custom/email/useEmailSelection";
import { BulkEmailActionBar } from "@/components/custom/email/BulkEmailActionBar";
import { useLoadOlder } from "@/components/custom/email/useLoadOlder";
import { useStickyPill } from "@/components/custom/email/useStickyPill";
import { matchesPillSticky, POPOUT_PILL_TO_SHARED } from "@/lib/email/stickyPill";
import { useEmailBody } from "@/components/custom/email/useEmailContent";
import { EmailAttachmentList } from "@/components/custom/email/EmailAttachmentList";
import { ClaimEmailDialog } from "@/components/custom/email/ClaimEmailDialog";
import { EmailForwardDialog } from "@/components/custom/email/EmailForwardDialog";
import { ComposeEmailDialog } from "@/components/custom/email/ComposeEmailDialog";
import { fromMyMailMessage } from "@/lib/email/types";
import type { MailFolder } from "@/lib/api";
import { OutlookCalendar } from "@/components/custom/calendar/OutlookCalendar";

type FilterMode = "all" | "unread" | "flagged" | "mine";
type PopoutModule = "mail" | "calendar";

const ACCENT = "#0f6cbd";
const MODULE_STORAGE_KEY = "centcom_popout_inbox_module";

// Sentinel selectedMailbox value for "My Mail" — a personal mailbox has no account_id, so it
// can never collide with a real mailbox_address string from /api/inbox.
const MY_MAIL_ID = "__my_mail__";

/** Outlook-style three-pane shared inbox for the pop-out window.
 * ponytail: reuses emailApi calls directly (same endpoints InboxEmailRow uses) rather than
 * importing InboxEmailRow's JSX, since its layout is row-shaped, not pane-shaped. */
export default function InboxOutlook() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUserId = user?.id ?? "";
  // Contractors can't send as a shared mailbox: no New email / Reply / Reply all / Forward.
  // The MakersHub forward and their own My Mail stay available.
  const canSend = user?.role !== "contractor";

  const [module, setModule] = useState<PopoutModule>(() => {
    try {
      const stored = localStorage.getItem(MODULE_STORAGE_KEY);
      return stored === "calendar" ? "calendar" : "mail";
    } catch {
      return "mail";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(MODULE_STORAGE_KEY, module);
    } catch {
      // localStorage unavailable (private window, etc.) — module choice just won't persist.
    }
  }, [module]);

  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null); // null = All Inboxes
  const [composeOpen, setComposeOpen] = useState(false);
  const { data: sendableMailboxes = [] } = useQuery({
    queryKey: ["sendable-mailboxes"],
    queryFn: () => emailApi.getSendableMailboxes(),
    staleTime: 5 * 60 * 1000,
  });
  // "Load older" pagination — per-mailbox pages beyond the main /api/inbox window, held in
  // component state (not React Query) so they survive the 60s poll. Shared with the flat
  // /inbox and the account page Shared Inbox tab: components/custom/email/useLoadOlder.
  const olderPages = useLoadOlder({
    logTag: "[inbox]",
    onError: () => toast({ title: "Failed to load older emails", variant: "destructive" }),
  });
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [reply, setReply] = useState<{ email: ClientEmail; accountId: string; replyAll?: boolean } | null>(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [pinMenuOpen, setPinMenuOpen] = useState(false);
  const [rowClaimOpenId, setRowClaimOpenId] = useState<string | null>(null);
  const [rowClaimSearch, setRowClaimSearch] = useState("");
  const [claimDialogEmail, setClaimDialogEmail] = useState<UnifiedEmail | null>(null);
  const [forwardDialogEmail, setForwardDialogEmail] = useState<UnifiedEmail | null>(null);
  const [categorizeOpen, setCategorizeOpen] = useState(false);
  const [newLabelInput, setNewLabelInput] = useState("");

  // ENG-96: rows this pane has optimistically patched stay listed until the pill or the
  // selected mailbox changes (epoch), so opening an email under Unread cannot make it
  // vanish out from under the cursor.
  const sticky = useStickyPill(`${filter}|${selectedMailbox ?? "*"}`);

  // Shared action hook (unification): optimistic patches land on the ["inbox"] cache,
  // so the old readOverrides/categoriesOverrides local-state machinery is gone.
  const actions = useEmailActions({ surface: "inbox" }, { onTouch: sticky.mark });

  const [researchRow, setResearchRow] = useState<{ message: InboxMessage; group: InboxGroup } | null>(null);
  const [researchTool, setResearchTool] = useState<"tax-research-memo" | "penalty-abatement">("tax-research-memo");
  const [researchContext, setResearchContext] = useState("");
  const [researchText, setResearchText] = useState("");
  const [researchRunning, setResearchRunning] = useState(false);
  const [researchDone, setResearchDone] = useState(false);
  const [researchSaving, setResearchSaving] = useState(false);
  const researchScrollRef = useRef<HTMLDivElement>(null);
  const researchCancelledRef = useRef({ current: false });

  const { data: categoryNamesData } = useQuery({
    queryKey: ["email-category-names"],
    queryFn: () => emailApi.getCategoryNames(),
    staleTime: Infinity, // ponytail: "once per session" — react-query cache covers this
  });

  // Gated on the active module: while Calendar is showing, the inbox groups query goes
  // inert (no poll, no background fetch) rather than burning a request every 60s for a
  // list nobody's looking at. Cached data is kept (react-query doesn't drop it just
  // because a query is disabled), so switching back to Mail renders instantly and the
  // re-enable kicks off exactly one refetch to catch up.
  const { data: allGroups = [], isLoading, refetch } = useQuery<InboxGroup[]>({
    queryKey: ["inbox"],
    queryFn: () => emailApi.getInbox(),
    enabled: module === "mail",
    refetchInterval: module === "mail" ? 60_000 : false,
  });

  // Per-user mailbox visibility — same hidden set as /inbox (shared localStorage key), so
  // "All Inboxes" totals and the folder rail exclude whatever the user has hidden.
  const [hiddenMailboxes, setMailboxHidden] = useHiddenMailboxes();
  const groups = useMemo(() => filterHiddenGroups(allGroups, hiddenMailboxes), [allGroups, hiddenMailboxes]);
  const [mailboxPickerOpen, setMailboxPickerOpen] = useState(false);
  const [mailboxSearch, setMailboxSearch] = useState("");
  const sortedGroups = useMemo(
    () => [...allGroups].sort((a, b) => a.account_name.localeCompare(b.account_name, undefined, { sensitivity: "base" })),
    [allGroups]
  );
  const mailboxSearchResults = useMemo(() => {
    const q = mailboxSearch.trim().toLowerCase();
    return q ? sortedGroups.filter((g) => g.account_name.toLowerCase().includes(q)) : sortedGroups;
  }, [sortedGroups, mailboxSearch]);

  const { data: users = [] } = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => api.getUsers({ assignable: true }),
    staleTime: 5 * 60_000,
  });

  const totalUnread = useMemo(() => groups.reduce((s, g) => s + (g.unread || 0), 0), [groups]);

  // If the selected mailbox gets hidden, fall back to "All Inboxes" rather than showing a dead pane.
  useEffect(() => {
    if (
      selectedMailbox &&
      selectedMailbox !== MY_MAIL_ID &&
      !groups.some((g) => g.mailbox_address === selectedMailbox)
    ) {
      setSelectedMailbox(null);
    }
  }, [groups, selectedMailbox]);

  // Flatten messages from the selected mailbox (or all), tagged with their owning group.
  const flatMessages = useMemo(() => {
    const src = selectedMailbox ? groups.filter((g) => g.mailbox_address === selectedMailbox) : groups;
    const rows: { message: InboxMessage; group: InboxGroup }[] = [];
    for (const g of src) {
      for (const m of g.messages) rows.push({ message: m, group: g });
      for (const m of olderPages.rowsByMailbox[g.mailbox_address] ?? []) rows.push({ message: m, group: g });
    }
    rows.sort((a, b) => new Date(b.message.receivedDateTime).getTime() - new Date(a.message.receivedDateTime).getTime());
    return rows;
  }, [groups, selectedMailbox, olderPages.rowsByMailbox, olderPages.token]);

  // v1.5.71: noise-rule matches for the selected SHARED mailbox. /api/inbox partitions them
  // into group.filtered; the pop-out used to read only g.messages, so rule-matched mail was
  // invisible and unrecoverable here. Single mailbox only (mirrors Load older); My Mail
  // has its own bucket in MyMailPanel.
  const filteredRows = useMemo(() => {
    if (!selectedMailbox || selectedMailbox === MY_MAIL_ID) return [];
    const g = groups.find((x) => x.mailbox_address === selectedMailbox);
    if (!g) return [];
    return (g.filtered ?? [])
      .map((m) => ({ message: m, group: g }))
      .sort((a, b) => new Date(b.message.receivedDateTime).getTime() - new Date(a.message.receivedDateTime).getTime());
  }, [groups, selectedMailbox]);
  const [filteredOpen, setFilteredOpen] = useState(false);
  const [sharedRulesOpen, setSharedRulesOpen] = useState(false);
  useEffect(() => { setFilteredOpen(false); }, [selectedMailbox]);

  const loadOlder = (mailbox: string) => {
    const group = groups.find((g) => g.mailbox_address === mailbox);
    if (!group) return;
    return olderPages.loadOlder([{ mailbox, baseRows: group.messages }]);
  };

  const categoriesFor = (m: InboxMessage): string[] => m.categories ?? [];

  const knownCategoryNames = useMemo(() => {
    const names = new Set(categoryNamesData?.names ?? []);
    for (const g of groups) for (const m of g.messages) for (const c of m.categories ?? []) names.add(c);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [categoryNamesData, groups]);

  // row → UnifiedEmail for the shared action hook.
  const toU = (row: { message: InboxMessage; group: InboxGroup }): UnifiedEmail =>
    fromInboxMessage(row.message, row.group);

  const toggleCategory = (row: { message: InboxMessage; group: InboxGroup }, name: string) => {
    const cur = categoriesFor(row.message);
    const next = cur.includes(name) ? cur.filter((c) => c !== name) : [...cur, name];
    actions.setCategories(toU(row), next);
  };

  const addNewLabel = (row: { message: InboxMessage; group: InboxGroup }) => {
    const name = newLabelInput.trim();
    if (!name) return;
    const cur = categoriesFor(row.message);
    if (!cur.includes(name)) actions.setCategories(toU(row), [...cur, name]);
    setNewLabelInput("");
  };

  const q = search.trim().toLowerCase();

  // ENG-96: search is NOT sticky - a row you opened still leaves the list when you type a
  // query it does not match (the reading pane keeps showing it - see `selected` below).
  const matchesSearchText = (u: UnifiedEmail): boolean => {
    if (!q) return true;
    const hay = `${u.subject ?? ""} ${u.fromName ?? ""} ${u.fromAddress ?? ""}`.toLowerCase();
    return hay.includes(q);
  };

  const unifiedRows = useMemo(
    () => flatMessages.map((row) => ({ row, u: toU(row) })),
    [flatMessages],
  );

  // Memoizing is safe because applyPatchToInboxGroups is fully immutable, so every optimistic
  // patch yields a new flatMessages identity. sticky.token is belt-and-braces: today every
  // sticky change also changes unifiedRows, so the memo would recompute anyway — the token
  // keeps that true if those two ever come apart (e.g. a sticky mark with no cache patch).
  const visibleRows = useMemo(
    () =>
      unifiedRows
        .filter(
          ({ u }) =>
            matchesPillSticky(u, POPOUT_PILL_TO_SHARED[filter], currentUserId, sticky.ids) &&
            matchesSearchText(u),
        )
        .map(({ row }) => row),
    // matchesSearchText is omitted from the deps because it closes over `q` AND NOTHING ELSE
    // — `q` is listed, so the memo is sound. If search ever gains a second input (a scope
    // selector, a "search body too" toggle, a debounced value), it MUST be added here or
    // visibleRows goes silently stale with no lint and no test to catch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unifiedRows, filter, currentUserId, q, sticky.token],
  );

  // ENG-96: resolve the reading pane from the UNFILTERED, mailbox-scoped list. A view
  // filter must never blank the pane or cancel the in-flight body fetch. Same shape
  // MyMailPanel already uses below. (flatMessages is mailbox-scoped, so switching
  // mailboxes still correctly blanks the pane.)
  const selected =
    flatMessages.find((r) => r.message.id === selectedMessageId) ??
    filteredRows.find((r) => r.message.id === selectedMessageId) ??
    null;
  const selectedU = selected ? toU(selected) : null;

  // Bulk multi-select. Derived DOWNSTREAM of visibleRows rather than inside its memo — that
  // memo is ENG-96 load-bearing (its sticky.token dependency is ratcheted) and must not grow
  // another job. The ids are the rows on screen, in list order, so a shift-range can only
  // ever span what the user can see.
  const visibleIds = useMemo(() => visibleRows.map((r) => r.message.id), [visibleRows]);
  const visibleUById = useMemo(
    () => new Map(visibleRows.map((r) => [r.message.id, toU(r)] as const)),
    [visibleRows],
  );

  // Epoch = pill + mailbox, matching the sticky epoch above: those are the two changes that
  // swap the list out from under the user. Search is deliberately excluded (it narrows the
  // same list, and visibleSelected already prunes whatever it hides).
  const selection = useEmailSelection(visibleIds, `${filter}|${selectedMailbox ?? "*"}`);

  const runBulk = async (action: BulkEmailAction) => {
    const emails = selection.visibleSelected
      .map((mid) => visibleUById.get(mid))
      .filter((e): e is UnifiedEmail => !!e);
    if (emails.length === 0) return;
    const { failed } = await actions.bulkApply(emails, action);
    // Leave the selection intact on a partial failure so the user can retry the same rows.
    if (failed === 0) selection.clear();
  };

  // Reading-pane body via the shared cached query; the server marks the message read
  // on this fetch (W4) — selectMessage patches the caches optimistically.
  const { data: body, isLoading: bodyLoading, isError: bodyError } = useEmailBody(
    selected?.group.mailbox_address ?? "",
    selected?.message.id ?? "",
    { enabled: !!selected, fallback: selected?.message.snippet },
  );

  // Active only (open/in_progress/on_hold) — closed occurrences of recurring work would
  // otherwise flood the pin menu with identical titles.
  const { data: projects = [] } = useQuery({
    queryKey: ["account-projects", selected?.group.account_id],
    queryFn: () => api.getProjects({ accountId: selected!.group.account_id, status: "active" }),
    enabled: pinMenuOpen && !!selected,
    staleTime: 60_000,
  });

  // Chrome-less pop-out: navigate via a real new tab in the main app, not in-place —
  // window.location would strand the standalone window on a page with no sidebar/nav.
  const openArchiveSearch = () => {
    const params = selectedMailbox ? `?mailbox=${encodeURIComponent(selectedMailbox)}` : "";
    window.open(`/email-search${params}`, "_blank");
  };

  const onRefresh = async () => {
    setRefreshing(true);
    sticky.reset(); // ENG-96 - explicit refresh re-evaluates every row against the pill
    selection.clear(); // a refresh can reorder/evict rows - never act on a stale selection
    try {
      await emailApi.getInbox(true);
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const selectMessage = (row: { message: InboxMessage; group: InboxGroup }) => {
    setSelectedMessageId(row.message.id);
    sticky.mark(row.message.id); // ENG-96 - keep the opened row listed under the pill
    // The body query fetch marks it read server-side (W4); patch the caches now.
    actions.markReadLocal(toU(row));
  };

  const openReply = (row: { message: InboxMessage; group: InboxGroup }, replyAll = false) => {
    setReply({ email: toReplyEmail(toU(row)), accountId: row.group.account_id, replyAll });
  };

  // "My Mail" (Phase 2): reply from the caller's own personal mailbox. No account context.
  const openMyMailReply = (email: UnifiedEmail, replyAll = false) => {
    setReply({ email: toReplyEmail(email), accountId: "", replyAll });
  };

  // "My Mail" rail entry: only shown once the firm has provisioned a personal mailbox for
  // this user (admin-only step — see AdminConfig). Folder tree also feeds the sub-rail.
  const { data: myMailFoldersData } = useQuery({
    queryKey: ["inbox-mine-folders"],
    queryFn: () => emailApi.getMyMailFolders(),
    staleTime: 60_000,
  });
  const myMailInboxUnread = myMailFoldersData?.folders.find((f) => f.path === "inbox")?.unreadCount ?? 0;

  const setReadState = (row: { message: InboxMessage; group: InboxGroup }, isRead: boolean) =>
    actions.setRead(toU(row), isRead);

  const claimRow = async (row: { message: InboxMessage; group: InboxGroup }, userId: string | null) => {
    const u = userId ? users.find((x) => x.id === userId) : null;
    await actions.claim(toU(row), u ? { id: u.id, name: u.name } : null);
    setRowClaimOpenId(null);
    setRowClaimSearch("");
  };

  const toggleFlag = () => {
    if (!selectedU) return;
    actions.toggleFlag(selectedU, selectedU.flagStatus !== "flagged");
  };

  const assign = (userId: string | null) => {
    if (!selectedU) return;
    const u = userId ? users.find((x) => x.id === userId) : null;
    actions.claim(selectedU, u ? { id: u.id, name: u.name } : null);
  };

  const pinTo = (projectId: string, projectTitle: string) => {
    if (!selectedU) return;
    actions.pinToProject(selectedU, { id: projectId, title: projectTitle });
  };

  const forwardToMakersHub = () => {
    if (!selectedU) return;
    actions.forwardToMakersHub(selectedU);
  };

  const busy = selectedU ? !!actions.busy[selectedU.id] : false;
  const filteredUsers = users.filter((u) => u.name.toLowerCase().includes(assignSearch.trim().toLowerCase()));
  const claimed = selected ? !!selected.message.taskStatus || !!selected.message.assignedToUserId : false;

  const openResearch = (row: { message: InboxMessage; group: InboxGroup }) => {
    setResearchRow(row);
    setResearchTool("tax-research-memo");
    setResearchContext("");
    setResearchText("");
    setResearchDone(false);
    researchCancelledRef.current.current = false;
  };

  const runResearch = async () => {
    if (!researchRow) return;
    const { message, group } = researchRow;
    setResearchRunning(true);
    setResearchDone(false);
    setResearchText("");
    researchCancelledRef.current.current = false;
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;

      const response = await fetch("/api/emails/research", {
        method: "POST",
        headers,
        body: JSON.stringify({
          mailbox: group.mailbox_address,
          message_id: message.id,
          tool: researchTool,
          context: researchContext.trim() || undefined,
        }),
      });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || json.message || `Error ${response.status}`);
      }
      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done || researchCancelledRef.current.current) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (raw === "[DONE]") break;
            try {
              const parsed = JSON.parse(raw);
              const delta = parsed?.choices?.[0]?.delta?.content;
              if (delta) {
                setResearchText((prev) => {
                  const next = prev + delta;
                  requestAnimationFrame(() => {
                    researchScrollRef.current?.scrollTo({ top: researchScrollRef.current.scrollHeight });
                  });
                  return next;
                });
              }
            } catch { /* skip malformed chunks */ }
          }
        }
      } finally {
        reader.cancel().catch(() => {});
      }
      setResearchDone(true);
    } catch (err: any) {
      toast({ title: "Research failed", description: err.message, variant: "destructive" });
    } finally {
      setResearchRunning(false);
    }
  };

  const copyResearch = async () => {
    try {
      await navigator.clipboard.writeText(researchText);
      toast({ title: "Copied to clipboard" });
    } catch (err: any) {
      toast({ title: "Could not copy", description: err.message, variant: "destructive" });
    }
  };

  const saveResearchToNotes = async () => {
    if (!researchRow) return;
    const { message, group } = researchRow;
    setResearchSaving(true);
    try {
      const label = researchTool === "tax-research-memo" ? "Tax Research Memo" : "Penalty Abatement Analysis";
      const notes = `[${label} — ${new Date().toLocaleDateString()}]\n\n${researchText}`;
      await emailApi.upsertEmailTask({
        graph_message_id: message.id,
        mailbox_address: group.mailbox_address,
        account_id: group.account_id,
        status: message.taskStatus,
        assigned_to_user_id: message.assignedToUserId ?? null,
        notes,
      });
      toast({ title: "Saved to the email's notes" });
      refetch();
    } catch (err: any) {
      toast({ title: "Could not save", description: err.message, variant: "destructive" });
    } finally {
      setResearchSaving(false);
    }
  };

  // One row renderer for the main list AND the Filtered bucket (v1.5.71) so the two can
  // never drift. Filtered rows are not selectable: bulk actions reach visibleRows only,
  // so a checkbox there would show "checked" for a row the bulk bar would never touch.
  const renderRow = (row: { message: InboxMessage; group: InboxGroup }, selectable = true) => {
    const { message: m } = row;
    const isSelected = m.id === selectedMessageId;
    const read = m.isRead;
    const claimed = !!(m.taskStatus || m.assignedToUserId);
    return (
      <ContextMenu key={m.id}>
      <ContextMenuTrigger asChild>
      <div
        className={cn(
          "group relative block w-full border-b border-neutral-100 border-l-[3px]",
          isSelected ? "bg-blue-50" : "hover:bg-neutral-50",
          !read ? "border-l-[color:var(--accent)]" : "border-l-transparent"
        )}
        style={{ ["--accent" as any]: ACCENT }}
      >
        {/* Multi-select. The WRAPPER owns the click so shiftKey is readable —
            Radix's onCheckedChange hands back a boolean, not the event, so a
            range-select would be impossible from the control itself. */}
        {selectable && (
          <div
            className={cn(
              "absolute left-1.5 top-2 z-10",
              !(selection.isSelected(m.id) || selection.active) &&
                "opacity-0 group-hover:opacity-100"
            )}
            onClick={(e) => {
              e.stopPropagation();
              selection.toggle(m.id, e.shiftKey);
            }}
            onMouseDown={(e) => {
              // Shift-click otherwise extends the browser's text selection across the list.
              if (e.shiftKey) e.preventDefault();
            }}
          >
            <Checkbox
              checked={selection.isSelected(m.id)}
              className="pointer-events-none bg-white"
              aria-label="Select email"
            />
          </div>
        )}
        <button
          // Ctrl/Cmd-click and shift-click select instead of opening — the Outlook
          // convention, and the only way to build a range without hunting for the
          // (hover-revealed) checkbox on every row.
          onClick={(e) => {
            if (selectable && (e.ctrlKey || e.metaKey)) { selection.toggle(m.id, false); return; }
            if (selectable && e.shiftKey) { selection.toggle(m.id, true); return; }
            selectMessage(row);
          }}
          onMouseDown={(e) => {
            if (e.shiftKey) e.preventDefault();
          }}
          // The left gutter is reserved UNCONDITIONALLY: the checkbox is hover-
          // revealed even with nothing selected, and a conditional pl would either
          // paint it over the sender name (review finding, v1.5.62) or jitter the
          // text on every hover.
          className="block w-full py-2 pl-7 pr-3 text-left"
        >
          <div className="flex items-center gap-1 pr-12">
            <span className={cn("truncate text-[13px]", !read ? "font-semibold" : "font-normal")}>
              {m.from?.name || m.from?.address || "(unknown)"}
            </span>
            <span className="ml-auto shrink-0 text-[11px] text-neutral-400">
              {shortWhen(m.receivedDateTime)}
            </span>
          </div>
          <div className={cn("truncate text-[13px]", !read ? "font-medium" : "text-neutral-700")}>
            {m.subject || "(no subject)"}
          </div>
          <div className="flex items-center gap-1.5 pr-12">
            <span className="truncate text-[12px] text-neutral-400">{m.snippet}</span>
            {m.flagStatus === "flagged" && <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />}
            {m.hasAttachments && <Paperclip className="h-3 w-3 shrink-0 text-neutral-400" />}
            {m.taskStatus && (
              <span
                title={emailTaskStatusLabel[m.taskStatus] ?? m.taskStatus}
                className={cn(
                  "shrink-0 rounded px-1 py-0 text-[9px] font-medium leading-tight",
                  statusChipClass(m.taskStatus)
                )}
              >
                {emailTaskStatusLabel[m.taskStatus] ?? m.taskStatus}
              </span>
            )}
            {m.assignedToName && (
              <span
                title={m.assignedToName}
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold text-white",
                  avatarColor(m.assignedToName)
                )}
              >
                {initialsFor(m.assignedToName)}
              </span>
            )}
            {/* ENG-98 + v1.5.69: "→ Jennifer +1" = transport-rule BCC copy;
                "Jennifer +1" = staff addressed alongside this shared mailbox. */}
            {(() => {
              const b = internalDeliveryBadge(m.originallyTo, m.internalRecipients);
              return b ? (
                <span
                  title={b.tooltip}
                  className="shrink-0 rounded border border-amber-300 bg-amber-50 px-1 py-0 text-[9px] font-medium leading-tight text-amber-800"
                >
                  {b.label}
                </span>
              ) : null;
            })()}
          </div>
          {categoriesFor(m).length > 0 && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {categoriesFor(m).slice(0, 2).map((c) => (
                <span key={c} className={cn("rounded px-1.5 py-0 text-[10px] font-medium", categoryColor(c))}>
                  {c}
                </span>
              ))}
              {categoriesFor(m).length > 2 && (
                <span className="text-[10px] text-neutral-400">+{categoriesFor(m).length - 2}</span>
              )}
            </div>
          )}
        </button>

        <div className="absolute right-2 top-2 flex items-center gap-0.5 opacity-90">
          <button
            title={read ? "Mark unread" : "Mark read"}
            onClick={(e) => {
              e.stopPropagation();
              setReadState(row, !read);
            }}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-200"
          >
            {read ? <MailOpen className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
          </button>
          <DropdownMenu
            open={rowClaimOpenId === m.id}
            onOpenChange={(open) => {
              setRowClaimOpenId(open ? m.id : null);
              if (!open) setRowClaimSearch("");
            }}
          >
            <DropdownMenuTrigger asChild>
              <button
                title={claimed ? "Reassign" : "Claim"}
                onClick={(e) => e.stopPropagation()}
                className={cn("rounded p-1 hover:bg-neutral-200", claimed ? "text-violet-600" : "text-neutral-500")}
              >
                <UserPlus className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuLabel>Claim / assign</DropdownMenuLabel>
              <div className="px-2 pb-1.5">
                <Input
                  autoFocus
                  value={rowClaimSearch}
                  onChange={(e) => setRowClaimSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search staff…"
                  className="h-7 text-xs"
                />
              </div>
              <DropdownMenuSeparator />
              <div className="max-h-56 overflow-auto">
                {users
                  .filter((u) => u.name.toLowerCase().includes(rowClaimSearch.trim().toLowerCase()))
                  .map((u) => (
                    <DropdownMenuItem key={u.id} onClick={() => claimRow(row, u.id)}>
                      {u.id === m.assignedToUserId && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                      <span className="truncate">{u.name}</span>
                    </DropdownMenuItem>
                  ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setClaimDialogEmail(toU(row)); setRowClaimOpenId(null); }}>
                {claimed ? "Reassign with notes…" : "Claim with notes…"}
              </DropdownMenuItem>
              {claimed && (
                <DropdownMenuItem onClick={() => claimRow(row, null)}>Unassign</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={() => selection.toggle(m.id, false)}>
          {selection.isSelected(m.id) ? "Deselect" : "Select"}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => setReadState(row, !read)}>
          {read ? "Mark as unread" : "Mark as read"}
        </ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>{claimed ? "Reassign" : "Claim / Assign"}</ContextMenuSubTrigger>
          <ContextMenuSubContent className="max-h-72 w-56 overflow-auto">
            {users.map((u) => (
              <ContextMenuItem key={u.id} onClick={() => claimRow(row, u.id)}>
                {u.id === m.assignedToUserId && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{u.name}</span>
              </ContextMenuItem>
            ))}
            {claimed && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => claimRow(row, null)}>Unassign</ContextMenuItem>
              </>
            )}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuItem onClick={() => actions.toggleFlag(toU(row), m.flagStatus !== "flagged")}>
          {m.flagStatus === "flagged" ? "Unflag" : "Flag"}
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            selectMessage(row);
            setPinMenuOpen(true);
          }}
        >
          Pin to project
        </ContextMenuItem>
        {canSend && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={() => { selectMessage(row); openReply(row); }}>Reply</ContextMenuItem>
            <ContextMenuItem onClick={() => { selectMessage(row); openReply(row, true); }}>Reply all</ContextMenuItem>
            <ContextMenuItem onClick={() => { selectMessage(row); setForwardDialogEmail(toU(row)); }}>Forward</ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuSub>
          <ContextMenuSubTrigger>Categorize</ContextMenuSubTrigger>
          <ContextMenuSubContent className="max-h-72 w-56 overflow-auto">
            {knownCategoryNames.length === 0 && (
              <ContextMenuItem disabled>No labels yet</ContextMenuItem>
            )}
            {knownCategoryNames.map((name) => (
              <ContextMenuItem key={name} onClick={() => toggleCategory(row, name)}>
                {categoriesFor(m).includes(name) && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                <span className={cn("mr-1.5 h-2 w-2 shrink-0 rounded-full", categoryColor(name).split(" ")[0])} />
                <span className="truncate">{name}</span>
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuSub>
          <ContextMenuSubTrigger>Research</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-56">
            <ContextMenuItem onClick={() => { selectMessage(row); openResearch(row); setResearchTool("tax-research-memo"); }}>
              Tax Research Memo
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { selectMessage(row); openResearch(row); setResearchTool("penalty-abatement"); }}>
              Penalty Abatement Analyzer
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenuContent>
      </ContextMenu>
    );
  };

  return (
    <div className="flex h-screen w-full bg-white text-[13px] text-neutral-900">
      {/* Far-left module rail — Mail / Calendar, Outlook-style */}
      <div className="flex w-11 shrink-0 flex-col items-center gap-1 border-r border-neutral-200 bg-[#f5f5f5] py-2">
        <button
          title="Mail"
          onClick={() => setModule("mail")}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded",
            module === "mail" ? "text-white" : "text-neutral-500 hover:bg-neutral-200"
          )}
          style={module === "mail" ? { backgroundColor: ACCENT } : undefined}
        >
          <Mail className="h-4 w-4" />
        </button>
        <button
          title="Calendar"
          onClick={() => setModule("calendar")}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded",
            module === "calendar" ? "text-white" : "text-neutral-500 hover:bg-neutral-200"
          )}
          style={module === "calendar" ? { backgroundColor: ACCENT } : undefined}
        >
          <CalendarDays className="h-4 w-4" />
        </button>
      </div>

      <div className="flex h-full min-h-0 flex-1 flex-col">
      {module === "calendar" ? (
        <OutlookCalendar />
      ) : (
      <>
      {/* Top bar */}
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-neutral-200 bg-white px-3">
        <span className="font-semibold" style={{ color: ACCENT }}>Shared Inbox</span>
        <span className="text-neutral-500">— Carpenter &amp; Company</span>
        <span className="ml-2 text-neutral-400">{totalUnread} unread</span>
        <DropdownMenu
          open={mailboxPickerOpen}
          onOpenChange={(open) => {
            setMailboxPickerOpen(open);
            if (!open) setMailboxSearch("");
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              title="Choose which client mailboxes show in your inbox"
              className="ml-3 flex h-7 items-center gap-1.5 rounded px-3 text-xs font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              <ListFilter className="h-3.5 w-3.5" />
              Choose mailboxes ({allGroups.length - allGroups.filter((g) => hiddenMailboxes.has(g.mailbox_address.toLowerCase())).length} of {allGroups.length} shown)
            </button>
          </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-96 w-64 overflow-y-auto">
                <DropdownMenuLabel>Tick the client mailboxes you want to see</DropdownMenuLabel>
                <div className="px-2 pb-1.5">
                  <Input
                    autoFocus
                    value={mailboxSearch}
                    onChange={(e) => setMailboxSearch(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Escape") {
                        if (mailboxSearch) {
                          e.preventDefault();
                          setMailboxSearch("");
                        }
                      }
                    }}
                    placeholder="Search mailboxes…"
                    className="h-7 text-xs"
                  />
                </div>
                <div className="flex items-center gap-2 px-2 pb-1.5 text-[11px]">
                  <button
                    type="button"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      allGroups.forEach((g) => setMailboxHidden(g.mailbox_address, false));
                    }}
                  >
                    Show all
                  </button>
                  <span className="text-neutral-300">·</span>
                  <button
                    type="button"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      allGroups.forEach((g) => setMailboxHidden(g.mailbox_address, true));
                    }}
                  >
                    Hide all
                  </button>
                </div>
                <DropdownMenuSeparator />
                {allGroups.length === 0 ? (
                  <DropdownMenuItem disabled>No mailboxes</DropdownMenuItem>
                ) : mailboxSearchResults.length === 0 ? (
                  <DropdownMenuItem disabled>No matching mailboxes</DropdownMenuItem>
                ) : (
                  mailboxSearchResults.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g.mailbox_address}
                      checked={!hiddenMailboxes.has(g.mailbox_address.toLowerCase())}
                      onCheckedChange={(checked) => setMailboxHidden(g.mailbox_address, !checked)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className="truncate text-xs">{g.account_name}</span>
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
        </DropdownMenu>
        {canSend && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1.5 text-neutral-600"
            onClick={() => setComposeOpen(true)}
          >
            <PenSquare className="h-3.5 w-3.5" />
            New email
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-neutral-600"
          onClick={openArchiveSearch}
          title={selectedMailbox ? `Search ${selectedMailbox}'s full archive` : "Search the full email archive"}
        >
          <MailSearch className="h-3.5 w-3.5" />
          Search archive
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-neutral-600"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* LEFT: folder pane */}
        <div className="flex w-[230px] shrink-0 flex-col overflow-y-auto border-r border-neutral-200 bg-[#f5f5f5] py-2">
          <button
            onClick={() => setSelectedMailbox(null)}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-1.5 text-left",
              selectedMailbox === null ? "font-semibold text-white" : "text-neutral-700 hover:bg-neutral-200"
            )}
            style={selectedMailbox === null ? { backgroundColor: ACCENT } : undefined}
          >
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            <span className="truncate">All Inboxes</span>
            {totalUnread > 0 && (
              <Badge className="ml-auto h-5 shrink-0 bg-neutral-700 hover:bg-neutral-700">{totalUnread}</Badge>
            )}
          </button>
          {myMailFoldersData?.mailbox && (
            <button
              onClick={() => setSelectedMailbox(MY_MAIL_ID)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left",
                selectedMailbox === MY_MAIL_ID ? "font-semibold text-white" : "text-neutral-700 hover:bg-neutral-200"
              )}
              style={selectedMailbox === MY_MAIL_ID ? { backgroundColor: ACCENT } : undefined}
              title="Your own mailbox — reply/forward/read only, no claim/pin/assign"
            >
              <Mail className="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span className="truncate">My Mail</span>
              {myMailInboxUnread > 0 && (
                <Badge
                  className={cn(
                    "ml-auto h-5 shrink-0",
                    selectedMailbox === MY_MAIL_ID ? "bg-white/20 hover:bg-white/20" : "bg-neutral-700 hover:bg-neutral-700"
                  )}
                >
                  {myMailInboxUnread}
                </Badge>
              )}
            </button>
          )}
          {groups.map((g) => (
            <button
              key={g.mailbox_address}
              onClick={() => setSelectedMailbox(g.mailbox_address)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left",
                selectedMailbox === g.mailbox_address
                  ? "font-semibold text-white"
                  : "text-neutral-700 hover:bg-neutral-200"
              )}
              style={selectedMailbox === g.mailbox_address ? { backgroundColor: ACCENT } : undefined}
            >
              <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span className="truncate">{g.account_name}</span>
              {g.unread > 0 && (
                <Badge
                  className={cn(
                    "ml-auto h-5 shrink-0",
                    selectedMailbox === g.mailbox_address ? "bg-white/20 hover:bg-white/20" : "bg-neutral-700 hover:bg-neutral-700"
                  )}
                >
                  {g.unread}
                </Badge>
              )}
            </button>
          ))}
        </div>

        {selectedMailbox === MY_MAIL_ID ? (
          <MyMailPanel currentUserId={currentUserId} onReply={openMyMailReply} />
        ) : (
        <>
        {/* MIDDLE: message list */}
        <div className="flex w-[420px] shrink-0 flex-col border-r border-neutral-200">
          <div className="shrink-0 border-b border-neutral-200 p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search mail"
                className="h-7 pl-7 text-xs"
              />
            </div>
            <div className="mt-1.5 flex gap-1">
              {(["all", "unread", "flagged", "mine"] as FilterMode[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] capitalize",
                    filter === f ? "text-white" : "text-neutral-600 hover:bg-neutral-100"
                  )}
                  style={filter === f ? { backgroundColor: ACCENT } : undefined}
                >
                  {f}
                </button>
              ))}
              {selectedMailbox && selectedMailbox !== MY_MAIL_ID && (
                <button
                  title={`Filter rules for ${selectedMailbox}`}
                  onClick={() => setSharedRulesOpen(true)}
                  className="ml-auto rounded p-1 text-neutral-500 hover:bg-neutral-100"
                >
                  <Filter className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          {/* Count = selection ∩ visible — the search box hides rows the action will not
              reach (review finding, v1.5.62). */}
          {selection.visibleSelected.length > 0 && (
            <div className="shrink-0 border-b border-neutral-200 bg-neutral-50 p-1.5">
              <BulkEmailActionBar
                dark
                count={selection.visibleSelected.length}
                users={users}
                currentUserId={currentUserId}
                knownCategories={knownCategoryNames}
                busy={actions.bulkBusy}
                onMarkRead={(isRead) => runBulk({ type: "read", isRead })}
                onAssign={(u) => runBulk({ type: "assign", user: u })}
                onFlag={(flagged) => runBulk({ type: "flag", flagged })}
                onCategorize={(label) => runBulk({ type: "categorize", label })}
                onClear={selection.clear}
              />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Filtered (N) sits at the TOP: below ~100 rows and Load older it was
                effectively invisible (v1.5.71 follow-up). */}
            {!isLoading && filteredRows.length > 0 && (
              <div className="border-b border-neutral-200 bg-neutral-50/60">
                <button
                  onClick={() => setFilteredOpen((v) => !v)}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[11px] font-medium text-neutral-500 hover:bg-neutral-50"
                >
                  {filteredOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  Filtered ({filteredRows.length})
                </button>
                {filteredOpen && filteredRows.map((row) => renderRow(row, false))}
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : allGroups.length > 0 && groups.length === 0 ? (
              <div className="px-4 py-10 text-center text-neutral-400">
                All mailboxes hidden — use the filter icon above to show some.
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="py-10 text-center text-neutral-400">No messages.</div>
            ) : (
              visibleRows.map((row) => renderRow(row))
            )}
            {/* "Load older" — only meaningful when a single mailbox is selected (All Inboxes
                combines mailboxes, so "oldest" is ambiguous there). Local state so it survives
                the 60s poll, which only ever refreshes ["inbox"]. */}
            {!isLoading && selectedMailbox && !olderPages.isExhausted(selectedMailbox) && (
              <div className="border-t border-neutral-100 px-3 py-2 text-center">
                <button
                  disabled={olderPages.isLoading(selectedMailbox)}
                  onClick={() => loadOlder(selectedMailbox)}
                  className="text-[11px] text-neutral-500 hover:text-neutral-800 disabled:opacity-60"
                >
                  {olderPages.isLoading(selectedMailbox) ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading older…
                    </span>
                  ) : (
                    "Load older"
                  )}
                </button>
              </div>
            )}
            {!isLoading && selectedMailbox && olderPages.isExhausted(selectedMailbox) && (
              <div className="border-t border-neutral-100 px-3 py-2 text-center text-[11px] text-neutral-400">
                No older emails.
              </div>
            )}
            <MailboxFilterRulesDialog
              mailbox={selectedMailbox}
              open={sharedRulesOpen}
              onClose={() => {
                setSharedRulesOpen(false);
                refetch(); // rule writes bust the server inbox cache
              }}
            />
          </div>
        </div>

        {/* RIGHT: reading pane */}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          {!selected ? (
            <div className="flex flex-1 items-center justify-center text-neutral-400">Select an item to read</div>
          ) : (
            <>
              <div className="shrink-0 border-b border-neutral-200 p-4">
                <h1 className="text-lg font-semibold">{selected.message.subject || "(no subject)"}</h1>
                {categoriesFor(selected.message).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {categoriesFor(selected.message).map((c) => (
                      <span
                        key={c}
                        className={cn("flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium", categoryColor(c))}
                      >
                        {c}
                        <button
                          onClick={() => toggleCategory(selected, c)}
                          className="rounded-full p-0.5 hover:bg-black/10"
                          title={`Remove ${c}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {(selected.message.from?.name || selected.message.from?.address || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {selected.message.from?.name || selected.message.from?.address}
                    </div>
                    {/* ENG-98: show the REAL To line when the archive has it — this header used
                        to hardcode the shared mailbox as the recipient, which is false for every
                        transport-rule BCC copy. "copied to" names the routing explicitly. */}
                    <div className="truncate text-xs text-neutral-500">
                      To{" "}
                      {(selected.message.toRecipients?.length ?? 0) > 0
                        ? selected.message.toRecipients!.map((r) => r.name || r.address).join(", ")
                        : selected.group.mailbox_address}
                      {(() => {
                        // v1.5.69: "copied to" keys on the mailbox being absent from To/Cc, not
                        // on the (now internal-only) originallyTo tag — external-only routed mail
                        // still gets the note. Both lists empty = can't know, so say nothing.
                        const rcpts = [
                          ...(selected.message.toRecipients ?? []),
                          ...(selected.message.ccRecipients ?? []),
                        ];
                        const mb = selected.group.mailbox_address.toLowerCase();
                        const routedIn =
                          rcpts.length > 0 &&
                          !rcpts.some((r) => (r.address ?? "").toLowerCase() === mb);
                        return routedIn ? ` · copied to ${selected.group.mailbox_address}` : null;
                      })()} ·{" "}
                      {new Date(selected.message.receivedDateTime).toLocaleString()}
                    </div>
                  </div>
                </div>

                {selectedU?.hasAttachments && (
                  <div className="border-t border-neutral-100 pt-1">
                    {/* Shared list: in-app preview modal + download + per-attachment → MH */}
                    <EmailAttachmentList email={selectedU} actions={actions} />
                  </div>
                )}

                {/* Action toolbar */}
                <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-neutral-100 pt-2">
                  {canSend && (
                    <>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={() => openReply(selected)}>
                        <Reply className="h-3.5 w-3.5 text-blue-600" /> Reply
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={() => openReply(selected, true)}>
                        <ReplyAll className="h-3.5 w-3.5 text-blue-600" /> Reply all
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={() => setForwardDialogEmail(toU(selected))}>
                        <Forward className="h-3.5 w-3.5 text-blue-600" /> Forward
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5"
                    onClick={() => setReadState(selected, !selected.message.isRead)}
                  >
                    {selected.message.isRead ? (
                      <MailOpen className="h-3.5 w-3.5 text-neutral-500" />
                    ) : (
                      <Mail className="h-3.5 w-3.5 text-neutral-500" />
                    )}
                    {selected.message.isRead ? "Mark unread" : "Mark read"}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={toggleFlag}>
                    <Star
                      className={cn(
                        "h-3.5 w-3.5",
                        selected.message.flagStatus === "flagged" ? "fill-amber-400 text-amber-400" : "text-neutral-500"
                      )}
                    />
                    {selected.message.flagStatus === "flagged" ? "Unflag" : "Flag"}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={() => openResearch(selected)}>
                    <BookOpenCheck className="h-3.5 w-3.5 text-violet-600" /> Research
                  </Button>

                  <DropdownMenu
                    open={categorizeOpen}
                    onOpenChange={(open) => {
                      setCategorizeOpen(open);
                      if (!open) setNewLabelInput("");
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5">
                        <Tag className={cn("h-3.5 w-3.5", categoriesFor(selected.message).length > 0 ? "text-violet-600" : "text-neutral-500")} />
                        Categorize
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-80 w-60 overflow-auto">
                      <DropdownMenuLabel>Labels</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {knownCategoryNames.length === 0 && <DropdownMenuItem disabled>No labels yet</DropdownMenuItem>}
                      {knownCategoryNames.map((name) => (
                        <DropdownMenuItem key={name} onSelect={(e) => { e.preventDefault(); toggleCategory(selected, name); }}>
                          {categoriesFor(selected.message).includes(name) && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                          <span className={cn("mr-1.5 h-2 w-2 shrink-0 rounded-full", categoryColor(name).split(" ")[0])} />
                          <span className="truncate">{name}</span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <div className="flex items-center gap-1 px-2 py-1.5">
                        <Input
                          value={newLabelInput}
                          onChange={(e) => setNewLabelInput(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") addNewLabel(selected);
                          }}
                          placeholder="New label…"
                          className="h-7 text-xs"
                        />
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu open={pinMenuOpen} onOpenChange={setPinMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5" disabled={busy}>
                        <Pin className={cn("h-3.5 w-3.5", selected.message.pinned ? "text-amber-600" : "text-neutral-500")} />
                        Pin
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-72 w-64 overflow-auto">
                      <DropdownMenuLabel>Pin to project</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {projects.length === 0 ? (
                        <DropdownMenuItem disabled>No active projects</DropdownMenuItem>
                      ) : (
                        projects.map((p) => {
                          // Server-computed period_display separates same-titled occurrences;
                          // pass it as the title so the optimistic chip matches the overlay.
                          const label = p.period_display ? `${p.title} — ${p.period_display}` : p.title;
                          return (
                            <DropdownMenuItem key={p.id} onClick={() => pinTo(p.id, label)}>
                              {label}
                            </DropdownMenuItem>
                          );
                        })
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu
                    open={assignMenuOpen}
                    onOpenChange={(open) => {
                      setAssignMenuOpen(open);
                      if (!open) setAssignSearch("");
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5" disabled={busy}>
                        <UserPlus className={cn("h-3.5 w-3.5", claimed ? "text-violet-600" : "text-neutral-500")} />
                        {claimed ? firstNameOf(selected.message.assignedToName, "") || "Claimed" : "Claim"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-72">
                      <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                      <div className="px-2 pb-1.5">
                        <Input
                          autoFocus
                          value={assignSearch}
                          onChange={(e) => setAssignSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Search staff…"
                          className="h-7 text-xs"
                        />
                      </div>
                      <DropdownMenuSeparator />
                      <div className="max-h-56 overflow-auto">
                        {filteredUsers.length === 0 ? (
                          <DropdownMenuItem disabled>No matching staff</DropdownMenuItem>
                        ) : (
                          filteredUsers.map((u) => (
                            <DropdownMenuItem key={u.id} onClick={() => assign(u.id)}>
                              {u.id === selected.message.assignedToUserId && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                              <span className="truncate">{u.name}</span>
                            </DropdownMenuItem>
                          ))
                        )}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => selectedU && setClaimDialogEmail(selectedU)}>
                        {claimed ? "Reassign with notes…" : "Claim with notes…"}
                      </DropdownMenuItem>
                      {claimed && <DropdownMenuItem onClick={() => assign(null)}>Unassign</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* 4-state status (account-page parity) */}
                  <select
                    className="h-7 cursor-pointer rounded border border-neutral-200 bg-white px-1.5 text-xs text-neutral-700"
                    value={selected.message.taskStatus ?? ""}
                    onChange={(e) => e.target.value && selectedU && actions.setStatus(selectedU, e.target.value)}
                    title="Email task status"
                  >
                    <option value="" disabled>Status…</option>
                    {EMAIL_TASK_STATUSES.map((s) => (
                      <option key={s} value={s}>{emailTaskStatusLabel[s]}</option>
                    ))}
                  </select>

                  {selected.group.makershubForwardConfigured && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1.5 text-amber-700"
                      onClick={forwardToMakersHub}
                      disabled={busy}
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      MakersHub
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 p-4">
                {bodyLoading ? (
                  <div className="flex items-center gap-2 text-neutral-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading message…
                  </div>
                ) : bodyError ? (
                  <pre className="whitespace-pre-wrap text-[13px] italic text-neutral-500">
                    {selected.message.snippet || "(could not load message)"}
                  </pre>
                ) : body?.contentType === "html" ? (
                  <EmailHtmlBody className="prose prose-sm max-w-none" html={body.content} />
                ) : (
                  <pre className="whitespace-pre-wrap text-[13px] text-neutral-800">{body?.content}</pre>
                )}
              </div>
            </>
          )}
        </div>
        </>
        )}
      </div>
      </>
      )}

      <EmailReplyModal
        email={reply?.email ?? null}
        accountId={reply?.accountId ?? ""}
        open={!!reply}
        onClose={() => setReply(null)}
        onSent={() => refetch()}
        replyAll={reply?.replyAll}
      />

      {/* Shared dialogs (unification) */}
      <EmailForwardDialog email={forwardDialogEmail} onClose={() => setForwardDialogEmail(null)} />
      <ClaimEmailDialog
        email={claimDialogEmail}
        users={users}
        actions={actions}
        onClose={() => setClaimDialogEmail(null)}
      />

      <Dialog
        open={!!researchRow}
        onOpenChange={(o) => {
          if (o) return;
          researchCancelledRef.current.current = true;
          setResearchRow(null);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Research</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
            {!researchText && !researchRunning ? (
              <>
                <RadioGroup value={researchTool} onValueChange={(v) => setResearchTool(v as typeof researchTool)}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                      researchTool === "tax-research-memo" ? "border-violet-500 bg-violet-50" : "border-neutral-200"
                    )}
                  >
                    <RadioGroupItem value="tax-research-memo" className="mt-0.5" />
                    <span>
                      <span className="block font-medium">Tax Research Memo</span>
                      <span className="block text-xs text-muted-foreground">
                        Researched, sourced answer to a technical tax/accounting/payroll question.
                      </span>
                    </span>
                  </label>
                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                      researchTool === "penalty-abatement" ? "border-violet-500 bg-violet-50" : "border-neutral-200"
                    )}
                  >
                    <RadioGroupItem value="penalty-abatement" className="mt-0.5" />
                    <span>
                      <span className="block font-medium">Penalty Abatement Analyzer</span>
                      <span className="block text-xs text-muted-foreground">
                        Pay-vs-fight analysis for a specific penalty notice.
                      </span>
                    </span>
                  </label>
                </RadioGroup>
                <div className="space-y-1">
                  <Label className="text-xs">Additional context <span className="text-muted-foreground">(optional)</span></Label>
                  <Textarea
                    value={researchContext}
                    onChange={(e) => setResearchContext(e.target.value)}
                    className="min-h-[80px] text-sm"
                    placeholder="Anything specific to point the research at…"
                  />
                </div>
              </>
            ) : (
              <>
                {researchRunning && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Researching live sources — this can take a minute…
                  </div>
                )}
                <div
                  ref={researchScrollRef}
                  className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm"
                >
                  {researchText || (researchRunning ? "" : "No output.")}
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground">AI research — review before anything goes to a client.</p>
          </div>
          <DialogFooter>
            {!researchText && !researchRunning ? (
              <>
                <Button variant="outline" onClick={() => setResearchRow(null)}>Cancel</Button>
                <Button onClick={runResearch}>Run</Button>
              </>
            ) : researchRunning ? (
              <Button variant="outline" disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={runResearch}>Run again</Button>
                <Button variant="outline" onClick={copyResearch}>Copy</Button>
                <Button onClick={saveResearchToNotes} disabled={researchSaving}>
                  {researchSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save to email notes
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ComposeEmailDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        fromOptions={sendableMailboxes}
        defaultFrom={selectedMailbox && selectedMailbox !== MY_MAIL_ID ? selectedMailbox : undefined}
        defaultAccountId={
          selectedMailbox
            ? (allGroups.find((g) => g.mailbox_address === selectedMailbox)?.account_id ?? null)
            : null
        }
        onSend={async (payload) => { await emailApi.composeEmail(payload); }}
      />
      </div>
    </div>
  );
}

/** "My Mail" (Phase 2, feature 1) — the caller's own personal mailbox. Deliberately a
 * self-contained, reduced-action panel (folder sub-rail + list + reading pane: reply/forward
 * and read/unread only) rather than woven into the shared claim/pin/assign pipeline above,
 * since none of those concepts apply here — the server rejects the writes anyway
 * (rejectIfPersonalMailboxTarget in routes.ts) and EmailRow/useEmailActions already gate on
 * isOwnPersonalMailbox for surfaces that DO share that pipeline. */
function MyMailPanel({
  currentUserId,
  onReply,
}: {
  currentUserId: string;
  onReply: (email: UnifiedEmail, replyAll?: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [folder, setFolder] = useState("inbox");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // My Mail pages /api/inbox/mine, not /api/inbox/older — it is the only surface whose
  // older pages carry a populated `filtered` (personal noise-rule) bucket, which is why the
  // shared hook takes an injectable fetcher and tracks both buckets.
  const olderPages = useLoadOlder({
    logTag: "[inbox/mine]",
    onError: () => toast({ title: "Failed to load older emails", variant: "destructive" }),
    fetchPage: ({ before }) => emailApi.getMyMail(folder, before),
    epochKey: folder,
  });
  const MY_MAIL_KEY = "__mine__";
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [filteredOpen, setFilteredOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // debounced value actually fetched
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParent, setNewFolderParent] = useState<string>("__root__");
  const [renameTarget, setRenameTarget] = useState<MailFolder | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MailFolder | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);

  const { data: foldersData } = useQuery({
    queryKey: ["inbox-mine-folders"],
    queryFn: () => emailApi.getMyMailFolders(),
    staleTime: 60_000,
  });
  const folders: MailFolder[] = foldersData?.folders ?? [];
  const refetchFolders = () => queryClient.invalidateQueries({ queryKey: ["inbox-mine-folders"] });

  // C1: "/" is the codebase's path-segment delimiter for flattened folder paths — reject it
  // client-side too (the server enforces this regardless, see validateFolderDisplayName).
  const invalidFolderName = (name: string) => !name || name !== name.trim() || name.includes("/");

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || name.includes("/")) return;
    setFolderBusy(true);
    try {
      await emailApi.createMyMailFolder(name, newFolderParent === "__root__" ? undefined : newFolderParent);
      toast({ title: `Folder "${name}" created` });
      setNewFolderOpen(false);
      setNewFolderName("");
      setNewFolderParent("__root__");
      refetchFolders();
    } catch (err: any) {
      toast({ title: "Could not create folder", description: err.message, variant: "destructive" });
    } finally {
      setFolderBusy(false);
    }
  };

  const renameFolder = async () => {
    if (!renameTarget?.id) return;
    const name = renameValue.trim();
    if (!name || name.includes("/")) return;
    setFolderBusy(true);
    try {
      await emailApi.renameMyMailFolder(renameTarget.id, name);
      toast({ title: "Folder renamed" });
      setRenameTarget(null);
      refetchFolders();
    } catch (err: any) {
      toast({ title: "Could not rename folder", description: err.message, variant: "destructive" });
    } finally {
      setFolderBusy(false);
    }
  };

  const deleteFolder = async () => {
    if (!deleteTarget?.id) return;
    setFolderBusy(true);
    try {
      await emailApi.deleteMyMailFolder(deleteTarget.id);
      toast({ title: `Moved "${deleteTarget.label}" to Deleted Items` });
      if (folder === deleteTarget.path) setFolder("inbox");
      setDeleteTarget(null);
      refetchFolders();
    } catch (err: any) {
      toast({ title: "Could not delete folder", description: err.message, variant: "destructive" });
    } finally {
      setFolderBusy(false);
    }
  };

  const { data: mailData, isLoading, refetch } = useQuery({
    queryKey: ["inbox-mine", folder],
    queryFn: () => emailApi.getMyMail(folder),
  });
  const mailbox = mailData?.mailbox ?? null;

  // Debounce the search box 300ms; Enter (see the input's onKeyDown) fires immediately.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const isSearching = searchQuery.length > 0;
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ["inbox-mine-search", searchQuery],
    queryFn: () => emailApi.searchMyMail(searchQuery),
    enabled: isSearching,
  });
  const searchResults = searchData?.messages ?? [];

  // Reset selection when the folder changes. Loaded pages reset themselves: the hook
  // takes `folder` as its epoch key and empties during the render that observes the
  // change, so no frame paints the previous folder's older rows.
  useEffect(() => {
    setSelectedId(null);
  }, [folder]);

  const rows = useMemo(() => {
    const base = [...(mailData?.messages ?? []), ...(olderPages.rowsByMailbox[MY_MAIL_KEY] ?? [])];
    base.sort((a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime());
    return base;
  }, [mailData, olderPages.rowsByMailbox, olderPages.token]);

  const filteredRows = useMemo(() => {
    const base = [...(mailData?.filtered ?? []), ...(olderPages.filteredByMailbox[MY_MAIL_KEY] ?? [])];
    base.sort((a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime());
    return base;
  }, [mailData, olderPages.filteredByMailbox, olderPages.token]);

  const selected =
    rows.find((m) => m.id === selectedId) ??
    filteredRows.find((m) => m.id === selectedId) ??
    searchResults.find((m) => m.id === selectedId) ??
    null;
  const selectedU = selected && mailbox ? fromMyMailMessage(selected, mailbox) : null;

  const { data: body, isLoading: bodyLoading, isError: bodyError } = useEmailBody(
    mailbox ?? "",
    selected?.id ?? "",
    { enabled: !!selected && !!mailbox, fallback: selected?.snippet },
  );

  const [readOverrides, setReadOverrides] = useState<Record<string, boolean>>({});
  const isRead = (m: InboxMessage) => readOverrides[m.id] ?? m.isRead;

  const setReadState = async (m: InboxMessage, next: boolean) => {
    if (!mailbox) return;
    setReadOverrides((prev) => ({ ...prev, [m.id]: next }));
    try {
      await emailApi.markRead(mailbox, m.id, next);
    } catch (err: any) {
      setReadOverrides((prev) => ({ ...prev, [m.id]: m.isRead })); // revert
      toast({ title: "Could not update read state", description: err.message, variant: "destructive" });
    }
  };

  const selectMessage = (m: InboxMessage) => {
    setSelectedId(m.id);
    if (!isRead(m)) setReadState(m, true); // fetching the body marks it read server-side too
  };

  /** Remove a message from every local list it might currently be sitting in — used
   * optimistically by Move To, ahead of the server's real move. */
  const removeMessageLocally = (id: string) => {
    olderPages.removeRow(id);
    queryClient.setQueryData(["inbox-mine", folder], (prev: any) =>
      prev
        ? {
            ...prev,
            messages: (prev.messages ?? []).filter((m: InboxMessage) => m.id !== id),
            filtered: (prev.filtered ?? []).filter((m: InboxMessage) => m.id !== id),
          }
        : prev,
    );
    if (selectedId === id) setSelectedId(null);
  };

  const moveMessageTo = async (m: InboxMessage, dest: MailFolder) => {
    if (!mailbox || !dest.id) return;
    removeMessageLocally(m.id); // optimistic — the row's stable id is about to change anyway
    try {
      await emailApi.moveMyMailMessage(mailbox, m.id, dest.id);
      toast({ title: `Moved to ${dest.label}` });
      refetchFolders(); // unread counts shift on both the source and destination folder
    } catch (err: any) {
      toast({ title: "Could not move message", description: err.message, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["inbox-mine", folder] });
    }
  };

  const loadOlder = () => {
    if (!mailbox) return;
    // One synthetic key: My Mail is a single mailbox, so the hook's per-mailbox records
    // degenerate to one entry. The cursor still spans BOTH buckets, which is what lets a
    // locally-held filtered row older than the oldest visible one page correctly.
    return olderPages.loadOlder([
      { mailbox: MY_MAIL_KEY, baseRows: [...(mailData?.messages ?? []), ...(mailData?.filtered ?? [])] },
    ]);
  };

  return (
    <>
      {/* Folder sub-rail: Inbox/Sent are kept live by the 5-min delta sync; any other folder
          is backfill-only (see server/lib/emailAccounts.ts) — the amber dot says so. */}
      <div className="flex w-[190px] shrink-0 flex-col overflow-y-auto border-r border-neutral-200 bg-[#fafafa] py-2">
        <div className="flex items-center justify-between px-3 pb-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Folders</span>
          <button
            title="New folder"
            onClick={() => setNewFolderOpen(true)}
            className="rounded p-0.5 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {folders.length === 0 && !mailbox ? (
          <div className="px-3 py-2 text-xs text-neutral-400">No personal mailbox is set up for you yet — ask an admin.</div>
        ) : (
          folders.map((f) => (
            <div key={f.path} className="group relative flex w-full items-center">
              <button
                onClick={() => setFolder(f.path)}
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left text-xs",
                  folder === f.path ? "font-semibold text-white" : "text-neutral-700 hover:bg-neutral-200"
                )}
                style={folder === f.path ? { backgroundColor: ACCENT } : undefined}
                title={f.live ? undefined : "Not kept live — synced only by a manual backfill"}
              >
                <span className="truncate">{f.label}</span>
                {!f.live && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" title="Not live — backfill only" />}
                {f.unreadCount > 0 && (
                  <Badge
                    className={cn(
                      "ml-auto h-5 shrink-0",
                      folder === f.path ? "bg-white/20 hover:bg-white/20" : "bg-neutral-700 hover:bg-neutral-700"
                    )}
                  >
                    {f.unreadCount}
                  </Badge>
                )}
              </button>
              {f.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      title="Folder options"
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        "mr-1 shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100",
                        folder === f.path ? "text-white/80 hover:bg-white/20" : "text-neutral-500 hover:bg-neutral-200"
                      )}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameTarget(f);
                        setRenameValue(f.label);
                      }}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleteTarget(f)} className="text-red-600 focus:text-red-600">
                      <Trash2 className="mr-2 h-3.5 w-3.5" /> Move to Deleted Items
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))
        )}
      </div>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-folder-name">Name</Label>
              <Input
                id="new-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Blue Diamond"
                autoFocus
              />
              {newFolderName.includes("/") && (
                <p className="mt-1 text-xs text-rose-600">Folder names can't contain "/"</p>
              )}
            </div>
            {folders.length > 0 && (
              <div>
                <Label htmlFor="new-folder-parent">Parent folder (optional)</Label>
                <select
                  id="new-folder-parent"
                  value={newFolderParent}
                  onChange={(e) => setNewFolderParent(e.target.value)}
                  className="mt-1 w-full rounded border border-neutral-300 bg-white px-2 py-1.5 text-sm"
                >
                  <option value="__root__">(mailbox root)</option>
                  {folders
                    .filter((f) => f.id)
                    .map((f) => (
                      <option key={f.path} value={f.path}>
                        {f.path}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createFolder} disabled={folderBusy || invalidFolderName(newFolderName.trim())}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
          {renameValue.includes("/") && (
            <p className="mt-1 text-xs text-rose-600">Folder names can't contain "/"</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={renameFolder} disabled={folderBusy || invalidFolderName(renameValue.trim())}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete (move to Deleted Items) confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Move "{deleteTarget?.label}" to Deleted Items?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            This moves the folder and everything in it to Deleted Items in Outlook — it does not permanently delete anything.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteFolder} disabled={folderBusy}>
              Move to Deleted Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MIDDLE: message list (reduced actions — no claim/pin/waiting-on/MakersHub) */}
      <div className="flex w-[420px] shrink-0 flex-col border-r border-neutral-200">
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-3 py-1.5">
          <span className="text-xs font-medium capitalize text-neutral-600">{folder}</span>
          <button
            title="Filter rules for your mail — hide senders/subjects you don't want to see"
            onClick={() => setRulesDialogOpen(true)}
            className="flex items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-0.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
          >
            <Filter className="h-3.5 w-3.5" /> Filter rules
          </button>
        </div>
        <div className="shrink-0 border-b border-neutral-200 p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearchQuery(searchInput.trim());
                if (e.key === "Escape") {
                  setSearchInput("");
                  setSearchQuery("");
                }
              }}
              placeholder="Search your mail"
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
        {isSearching && (
          <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 bg-neutral-50 px-3 py-1 text-[11px] text-neutral-500">
            <span>Search results {!searchLoading && `(${searchResults.length})`}</span>
            <button
              className="font-medium text-neutral-600 hover:text-neutral-900"
              onClick={() => {
                setSearchInput("");
                setSearchQuery("");
              }}
            >
              Clear
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isSearching ? (
            searchLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            ) : searchResults.length === 0 ? (
              <div className="py-10 text-center text-neutral-400">No matches.</div>
            ) : (
              searchResults.map((m) => {
                const read = isRead(m);
                const selectedRow = m.id === selectedId;
                return (
                  <button
                    key={m.id}
                    onClick={() => selectMessage(m)}
                    className={cn(
                      "block w-full border-b border-neutral-100 border-l-[3px] px-3 py-2 text-left",
                      selectedRow ? "bg-blue-50" : "hover:bg-neutral-50",
                      !read ? "border-l-[color:var(--accent)]" : "border-l-transparent"
                    )}
                    style={{ ["--accent" as any]: ACCENT }}
                  >
                    <div className="flex items-center gap-1 pr-2">
                      <span className={cn("truncate text-[13px]", !read ? "font-semibold" : "font-normal")}>
                        {m.from?.name || m.from?.address || "(unknown)"}
                      </span>
                      <span className="ml-auto shrink-0 text-[11px] text-neutral-400">{shortWhen(m.receivedDateTime)}</span>
                    </div>
                    <div className={cn("truncate text-[13px]", !read ? "font-medium" : "text-neutral-700")}>
                      {m.subject || "(no subject)"}
                    </div>
                    <div className="flex items-center gap-1.5 pr-2">
                      <span className="truncate text-[12px] text-neutral-400">{m.snippet}</span>
                      {m.hasAttachments && <Paperclip className="h-3 w-3 shrink-0 text-neutral-400" />}
                    </div>
                  </button>
                );
              })
            )
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-neutral-400">No messages.</div>
          ) : (
            rows.map((m) => {
              const read = isRead(m);
              const selectedRow = m.id === selectedId;
              return (
                <div
                  key={m.id}
                  className={cn(
                    "group relative block w-full border-b border-neutral-100 border-l-[3px]",
                    selectedRow ? "bg-blue-50" : "hover:bg-neutral-50",
                    !read ? "border-l-[color:var(--accent)]" : "border-l-transparent"
                  )}
                  style={{ ["--accent" as any]: ACCENT }}
                >
                  <button onClick={() => selectMessage(m)} className="block w-full px-3 py-2 text-left">
                    <div className="flex items-center gap-1 pr-10">
                      <span className={cn("truncate text-[13px]", !read ? "font-semibold" : "font-normal")}>
                        {m.from?.name || m.from?.address || "(unknown)"}
                      </span>
                      <span className="ml-auto shrink-0 text-[11px] text-neutral-400">{shortWhen(m.receivedDateTime)}</span>
                    </div>
                    <div className={cn("truncate text-[13px]", !read ? "font-medium" : "text-neutral-700")}>
                      {m.subject || "(no subject)"}
                    </div>
                    <div className="flex items-center gap-1.5 pr-10">
                      <span className="truncate text-[12px] text-neutral-400">{m.snippet}</span>
                      {m.hasAttachments && <Paperclip className="h-3 w-3 shrink-0 text-neutral-400" />}
                    </div>
                  </button>
                  <div className="absolute right-2 top-2 flex items-center gap-0.5 opacity-90">
                    {folders.some((f) => f.id && f.path !== folder) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            title="Move to…"
                            onClick={(e) => e.stopPropagation()}
                            className="rounded p-1 text-neutral-500 hover:bg-neutral-200"
                          >
                            <FolderInput className="h-3 w-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Move to</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {folders
                            .filter((f) => f.id && f.path !== folder)
                            .map((f) => (
                              <DropdownMenuItem key={f.path} onClick={() => moveMessageTo(m, f)}>
                                {f.label}
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <button
                      title={read ? "Mark unread" : "Mark read"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setReadState(m, !read);
                      }}
                      className="rounded p-1 text-neutral-500 hover:bg-neutral-200"
                    >
                      {read ? <MailOpen className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                    </button>
                  </div>
                </div>
              );
            })
          )}
          {!isLoading && rows.length > 0 && !olderPages.isExhausted(MY_MAIL_KEY) && (
            <div className="border-t border-neutral-100 px-3 py-2 text-center">
              <button
                disabled={olderPages.isLoading(MY_MAIL_KEY)}
                onClick={loadOlder}
                className="text-[11px] text-neutral-500 hover:text-neutral-800 disabled:opacity-60"
              >
                {olderPages.isLoading(MY_MAIL_KEY) ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading older…
                  </span>
                ) : (
                  "Load older"
                )}
              </button>
            </div>
          )}
          {!isLoading && olderPages.isExhausted(MY_MAIL_KEY) && (
            <div className="border-t border-neutral-100 px-3 py-2 text-center text-[11px] text-neutral-400">No older emails.</div>
          )}
          {!isLoading && filteredRows.length > 0 && (
            <div className="border-t border-neutral-200">
              <button
                onClick={() => setFilteredOpen((v) => !v)}
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[11px] font-medium text-neutral-500 hover:bg-neutral-50"
              >
                {filteredOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Filtered ({filteredRows.length})
              </button>
              {filteredOpen &&
                filteredRows.map((m) => {
                  const read = isRead(m);
                  const selectedRow = m.id === selectedId;
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        "group relative block w-full border-b border-neutral-100 border-l-[3px]",
                        selectedRow ? "bg-blue-50" : "hover:bg-neutral-50",
                        !read ? "border-l-[color:var(--accent)]" : "border-l-transparent"
                      )}
                      style={{ ["--accent" as any]: ACCENT }}
                    >
                      <button onClick={() => selectMessage(m)} className="block w-full px-3 py-2 text-left">
                        <div className="flex items-center gap-1 pr-10">
                          <span className={cn("truncate text-[13px]", !read ? "font-semibold" : "font-normal")}>
                            {m.from?.name || m.from?.address || "(unknown)"}
                          </span>
                          <span className="ml-auto shrink-0 text-[11px] text-neutral-400">{shortWhen(m.receivedDateTime)}</span>
                        </div>
                        <div className={cn("truncate text-[13px]", !read ? "font-medium" : "text-neutral-700")}>
                          {m.subject || "(no subject)"}
                        </div>
                        <div className="flex items-center gap-1.5 pr-10">
                          <span className="truncate text-[12px] text-neutral-400">{m.snippet}</span>
                          {m.hasAttachments && <Paperclip className="h-3 w-3 shrink-0 text-neutral-400" />}
                        </div>
                      </button>
                      <div className="absolute right-2 top-2 flex items-center gap-0.5 opacity-90">
                        <button
                          title={read ? "Mark unread" : "Mark read"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReadState(m, !read);
                          }}
                          className="rounded p-1 text-neutral-500 hover:bg-neutral-200"
                        >
                          {read ? <MailOpen className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <MailboxFilterRulesDialog
        mailbox={mailbox}
        open={rulesDialogOpen}
        hideApplyToAll
        onClose={() => {
          setRulesDialogOpen(false);
          refetch();
        }}
      />

      {/* RIGHT: reading pane */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        {!selected ? (
          <div className="flex flex-1 items-center justify-center text-neutral-400">Select an item to read</div>
        ) : (
          <>
            <div className="shrink-0 border-b border-neutral-200 p-4">
              <h1 className="text-lg font-semibold">{selected.subject || "(no subject)"}</h1>
              <div className="mt-2 flex items-center gap-2">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: ACCENT }}
                >
                  {(selected.from?.name || selected.from?.address || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{selected.from?.name || selected.from?.address}</div>
                  <div className="truncate text-xs text-neutral-500">
                    {mailbox} · {new Date(selected.receivedDateTime).toLocaleString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto gap-1.5 text-neutral-600"
                  onClick={() => selectedU && onReply(selectedU)}
                >
                  <Reply className="h-3.5 w-3.5" /> Reply
                </Button>
              </div>
            </div>
            <div className="flex-1 p-4">
              {bodyLoading ? (
                <div className="flex items-center gap-2 text-neutral-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading message…
                </div>
              ) : bodyError ? (
                <pre className="whitespace-pre-wrap text-[13px] italic text-neutral-500">
                  {selected.snippet || "(could not load message)"}
                </pre>
              ) : body?.contentType === "html" ? (
                <EmailHtmlBody className="prose prose-sm max-w-none" html={body.content} />
              ) : (
                <pre className="whitespace-pre-wrap text-[13px] text-neutral-800">{body?.content}</pre>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
